// src/components/payment/PromoCodeInput.tsx
// Promo code section — hidden behind an "I have a promo code" toggle.
// Shown only after the user has selected a plan.
// Calls the validate-promo Netlify function and notifies parent of the result.

import React, { useRef, useState } from 'react';
import { validatePromoCode } from '../../services/paymentService';
import type { PlanId } from '../../stores/usePaymentStore';

// ── Props ─────────────────────────────────────────────────────────────────────

interface PromoCodeInputProps {
  /** The currently selected plan. Required for validate-promo. */
  selectedPlan: PlanId;
  /**
   * Called when a valid partial-discount promo is applied.
   * Parent uses this to update the CTA button price.
   */
  onPromoApplied: (promoCode: string, finalPrice: number) => void;
  /**
   * Called when a 100%-off promo is validated and the subscription is
   * activated server-side. Parent should show the success screen.
   */
  onFreePromoActivated: () => Promise<void>;
  /**
   * Called when the promo is reset (plan changed externally).
   * Also called internally when the user clears the input.
   */
  onPromoReset?: () => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function PromoCodeInput({
  selectedPlan,
  onPromoApplied,
  onFreePromoActivated,
  onPromoReset,
}: PromoCodeInputProps): React.JSX.Element {
  const [isExpanded, setIsExpanded]     = useState(false);
  const [code, setCode]                 = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [feedback, setFeedback]         = useState<{
    text: string;
    type: 'ok' | 'err' | 'info' | null;
  }>({ text: '', type: null });
  const [isValidCode, setIsValidCode]   = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleToggle = () => {
    setIsExpanded((prev) => !prev);
    if (!isExpanded) {
      // Focus input next frame when it becomes visible
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const upper = e.target.value.toUpperCase().slice(0, 32);
    setCode(upper);
    // Clear feedback when user types a new code
    if (feedback.type !== null) {
      setFeedback({ text: '', type: null });
      setIsValidCode(false);
      onPromoReset?.();
    }
  };

  const handleApply = async () => {
    const trimmed = code.trim();
    if (!trimmed) {
      setFeedback({ text: 'Please enter a promo code.', type: 'err' });
      return;
    }
    if (isValidating) return;

    setIsValidating(true);
    setFeedback({ text: 'Checking…', type: 'info' });

    try {
      const result = await validatePromoCode(trimmed, selectedPlan);

      if (!result.valid) {
        setIsValidCode(false);
        setFeedback({ text: result.message ?? 'Invalid or expired promo code.', type: 'err' });
        onPromoReset?.();
        return;
      }

      setIsValidCode(true);

      if (result.isFree) {
        // 100% discount — subscription activated server-side by validate-promo
        setFeedback({ text: '100% discount applied! Activating your access…', type: 'ok' });
        await onFreePromoActivated();
        return;
      }

      // Partial discount
      const finalPrice  = result.finalPrice ?? 0;
      const displayText = result.discountDisplay
        ? `${result.discountDisplay} applied — New price: $${finalPrice.toFixed(2)}`
        : `Discount applied — New price: $${finalPrice.toFixed(2)}`;
      setFeedback({ text: displayText, type: 'ok' });
      onPromoApplied(trimmed, finalPrice);
    } catch (err) {
      setIsValidCode(false);
      setFeedback({ text: 'Could not validate code. Please try again.', type: 'err' });
      onPromoReset?.();
    } finally {
      setIsValidating(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleApply();
    }
  };

  // ── Styles ──────────────────────────────────────────────────────────────────

  const styles: Record<string, React.CSSProperties> = {
    wrapper: {
      padding: '0 28px 20px',
    },

    toggle: {
      background: 'none',
      border: 'none',
      color: 'rgba(255,255,255,0.32)',
      fontSize: '0.78rem',
      cursor: 'pointer',
      padding: 0,
      textDecoration: 'underline',
      textUnderlineOffset: '2px',
      fontFamily: 'inherit',
      transition: 'color 0.2s ease',
    },

    inputRow: {
      display: isExpanded ? 'flex' : 'none',
      gap: '8px',
      marginTop: '10px',
      alignItems: 'stretch',
    },

    input: {
      flex: 1,
      background: 'rgba(255,255,255,0.05)',
      border: `1.5px solid ${
        isValidCode
          ? '#00b894'
          : feedback.type === 'err'
          ? '#e17055'
          : 'rgba(255,255,255,0.1)'
      }`,
      borderRadius: '10px',
      color: '#fff',
      fontFamily: 'inherit',
      fontSize: '0.88rem',
      padding: '10px 14px',
      outline: 'none',
      transition: 'border-color 0.2s ease',
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    },

    applyBtn: {
      padding: '10px 18px',
      background: isValidating
        ? 'rgba(108,92,231,0.1)'
        : 'rgba(108,92,231,0.18)',
      border: '1.5px solid rgba(108,92,231,0.35)',
      borderRadius: '10px',
      color: isValidating ? 'rgba(162,155,254,0.5)' : '#a29bfe',
      fontFamily: 'inherit',
      fontSize: '0.84rem',
      fontWeight: 700,
      cursor: isValidating ? 'not-allowed' : 'pointer',
      whiteSpace: 'nowrap',
      transition: 'all 0.2s ease',
    },

    feedback: {
      marginTop: '8px',
      fontSize: '0.79rem',
      minHeight: '18px',
      color:
        feedback.type === 'ok'
          ? '#00b894'
          : feedback.type === 'err'
          ? '#e17055'
          : 'rgba(255,255,255,0.4)',
    },
  };

  return (
    <div style={styles.wrapper}>
      <button
        type="button"
        style={styles.toggle}
        onClick={handleToggle}
        onMouseEnter={(e) => {
          (e.target as HTMLButtonElement).style.color = 'rgba(255,255,255,0.55)';
        }}
        onMouseLeave={(e) => {
          (e.target as HTMLButtonElement).style.color = 'rgba(255,255,255,0.32)';
        }}
      >
        {isExpanded ? 'Hide promo code field' : 'I have a promo code'}
      </button>

      <div style={styles.inputRow}>
        <input
          ref={inputRef}
          style={styles.input}
          type="text"
          value={code}
          onChange={handleCodeChange}
          onKeyDown={handleKeyDown}
          placeholder="PROMO CODE"
          maxLength={32}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          disabled={isValidating}
        />
        <button
          type="button"
          style={styles.applyBtn}
          onClick={handleApply}
          disabled={isValidating}
          onMouseEnter={(e) => {
            if (!isValidating) {
              (e.target as HTMLButtonElement).style.background = 'rgba(108,92,231,0.3)';
              (e.target as HTMLButtonElement).style.color = '#fff';
            }
          }}
          onMouseLeave={(e) => {
            if (!isValidating) {
              (e.target as HTMLButtonElement).style.background = 'rgba(108,92,231,0.18)';
              (e.target as HTMLButtonElement).style.color = '#a29bfe';
            }
          }}
        >
          {isValidating ? 'Checking…' : 'Apply'}
        </button>
      </div>

      {feedback.text && <div style={styles.feedback}>{feedback.text}</div>}
    </div>
  );
}